import { FC } from "react";
import { Divider, List } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppMenuKey, AppRoutes } from "../../../../../../common/routesUtils";
import { clearBreadcrumbsStorage } from "../../../../../../context/BreadcrumbsProvider";
import { clearFiltersStorage } from "../../../../../../context/FiltersProvider/utils/persistence";
import { usePageConfiguration } from "../../../../../../context/PageConfigrations/PageConfigurationProvider";
import { useAuthentication } from "../../../../../../hooks/useAuthentication";
import { usePreferences } from "../../../../../../hooks/usePreferences";
import { ThemeMode } from "../../../../../../state/preferences";
import { MenuIcon } from "../MenuIcon";
import ActionItem from "./ActionItem";
import SelectItem from "./SelectItem";

interface Props {
	menuIsOpen: boolean;
	closeMenu: () => void;
}

/**
 * MenuActions is the side menu that allows users to navigate main sections of GreenRouter UI app.
 *
 * @param menuIsOpen  the flag that indicates if the menu is open or closed
 * @param closeMenu   the handler used to close the menu
 * @returns           the application side menu
 */
const MenuActions: FC<Props> = ({ menuIsOpen }) => {
	const { t } = useTranslation();
	const navigateTo = useNavigate();
	const { signOut } = useAuthentication();
	const { preferences, changeTheme } = usePreferences();

	const { clearAllPageConfigurations } = usePageConfiguration();

	const performSignOut = () => {
		signOut();
		navigateTo(AppRoutes.SignIn);

		// @TO DO,
		// clera local storages data about filters
		clearFiltersStorage();

		// Clear breadcrumbs stored in local storage
		clearBreadcrumbsStorage();

		// Clear page configureations stored in local storage
		clearAllPageConfigurations();
	};

	return (
		<List>
			<Divider />
			<SelectItem<ThemeMode>
				icon={<MenuIcon target={AppMenuKey.Theme} />}
				menuIsOpen={menuIsOpen}
				onChange={changeTheme}
				value={preferences.theme ?? "system"}
				title={t("preferences/theme/title", { ns: "navigation" })}
				options={[
					{
						text: t(`preferences/theme/${preferences.systemTheme ?? "light"}`, {
							ns: "navigation",
							context: "system preference",
						}),
						value: "system",
						showDivider: true,
					},
					{
						text: t("preferences/theme/dark", { ns: "navigation", context: "option" }),
						value: "dark",
						icon: <MenuIcon target="dark" />,
					},
					{
						text: t("preferences/theme/light", { ns: "navigation", context: "option" }),
						value: "light",
						icon: <MenuIcon target="light" />,
					},
				]}
			/>

			{/* TODO: discuss if account should be included in side menu (currrently available in top bar)
                             <AppMenuItem item={{key: AppMenuKey.Account, route: AppRoutes.Account, items: []}} menuIsOpen={menuIsOpen}/>
                     */}

			<Divider />
			<ActionItem
				icon={<MenuIcon target={AppMenuKey.SignOut} />}
				menuIsOpen={menuIsOpen}
				onConfirm={performSignOut}
				title={t("actions/sign out/title", { ns: "navigation" })}
				confirmOptions={{ title: t("actions/sign out/ask confirm", { ns: "navigation" }) }}
			/>
		</List>
	);
};

export default MenuActions;
